/**
 * Phase INV-CAT (Gap 2) — Invoice Categories admin card for System
 * Settings (Operational Tools section).
 *
 * Backend contract:
 *   GET    /api/admin/settings/invoice-categories           (admin + super_admin)
 *   POST   /api/admin/settings/invoice-categories           (super_admin)
 *   PATCH  /api/admin/settings/invoice-categories/{cat_id}  (super_admin)
 *   DELETE /api/admin/settings/invoice-categories/{cat_id}  (super_admin)
 *      ↳ 409 if any invoice line references the cat_id.
 *
 * Behaviour:
 *   * Admin role: read-only list (no add/rename/delete controls).
 *   * Super-admin: full CRUD.
 *       - "+ Add category" button reveals an inline input.
 *       - Each row: label, Rename, Delete.
 *       - Rename uses an inline edit pattern (click label → input → save).
 *       - Rows with deletable: false get a "Locked" chip and no Delete.
 *       - Delete opens a confirmation modal. 409 from the API surfaces
 *         the helpful "N invoice(s) reference this category" detail
 *         inline (no toast).
 */
import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Tags, Lock, Pencil, Trash2, Plus, Check, X, Loader2 } from "lucide-react";

export default function InvoiceCategoriesCard({ userRole }) {
    const isSuper = userRole === "super_admin";
    const [cats, setCats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [newLabel, setNewLabel] = useState("");
    const [busy, setBusy] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editLabel, setEditLabel] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(null); // {id, label}
    const [deleteError, setDeleteError] = useState(null);
    // Phase INV-CAT (Fix 3) — when a row enters rename mode we want the
    // existing label to be focused AND fully selected so a typist's
    // first keystroke replaces it (otherwise the new chars concatenate
    // onto the old label, e.g. "Inspection" + "Inspection" →
    // "InspectionInspection"). The ref + useEffect pair fires whenever
    // ``editingId`` flips to a non-null value.
    const renameInputRef = useRef(null);

    useEffect(() => {
        if (editingId && renameInputRef.current) {
            renameInputRef.current.focus();
            try { renameInputRef.current.select(); } catch (_) { /* jsdom */ }
        }
    }, [editingId]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/settings/invoice-categories");
            setCats(data || []);
        } catch (err) {
            toast.error(formatApiError(err));
            setCats([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submitAdd = async () => {
        const lbl = newLabel.trim();
        if (!lbl) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/settings/invoice-categories", { label: lbl });
            setCats(data);
            setNewLabel("");
            setAdding(false);
            toast.success(`Category "${lbl}" added`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const submitRename = async (catId) => {
        const lbl = editLabel.trim();
        if (!lbl) { setEditingId(null); return; }
        setBusy(true);
        try {
            const { data } = await api.patch(
                `/admin/settings/invoice-categories/${catId}`, { label: lbl });
            setCats(data);
            setEditingId(null);
            toast.success("Category renamed");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const submitDelete = async () => {
        if (!confirmDelete) return;
        setBusy(true);
        setDeleteError(null);
        try {
            const { data } = await api.delete(
                `/admin/settings/invoice-categories/${confirmDelete.id}`);
            setCats(data);
            toast.success(`Category "${confirmDelete.label}" deleted`);
            setConfirmDelete(null);
        } catch (err) {
            // 409 ⇒ inline helpful message inside the modal, no toast.
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail || formatApiError(err);
            if (status === 409) {
                setDeleteError(detail);
            } else {
                toast.error(detail);
                setConfirmDelete(null);
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Card className="lg:col-span-2" data-testid="invoice-categories-card">
                <CardHeader>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                            <Tags size={16} className="text-blue-900" />
                            <CardTitle className="font-display text-xl">Invoice Categories</CardTitle>
                        </div>
                        {isSuper && !adding && (
                            <Button
                                onClick={() => setAdding(true)}
                                size="sm"
                                variant="outline"
                                className="gap-1.5"
                                data-testid="invoice-categories-add-open"
                            >
                                <Plus size={13} /> Add category
                            </Button>
                        )}
                    </div>
                    <CardDescription>
                        Drives the &ldquo;Category&rdquo; dropdown on the Create-invoice-from-rental modal.
                        ``Rent`` is the prefill-trigger category — renames are allowed, but
                        deletion is locked.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {loading && (
                        <p className="text-sm text-slate-500 inline-flex items-center gap-2"
                           data-testid="invoice-categories-loading">
                            <Loader2 size={13} className="animate-spin" /> Loading…
                        </p>
                    )}

                    {!loading && isSuper && adding && (
                        <div className="mb-3 flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50/40 p-2"
                             data-testid="invoice-categories-add-form">
                            <Input
                                autoFocus
                                value={newLabel}
                                onChange={(e) => setNewLabel(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") submitAdd();
                                    if (e.key === "Escape") { setAdding(false); setNewLabel(""); }
                                }}
                                placeholder="e.g. Cleaning"
                                disabled={busy}
                                className="h-8 text-sm"
                                data-testid="invoice-categories-add-input"
                                maxLength={80}
                            />
                            <Button
                                onClick={submitAdd}
                                size="sm"
                                disabled={busy || !newLabel.trim()}
                                className="bg-blue-900 hover:bg-blue-800 gap-1"
                                data-testid="invoice-categories-add-save"
                            >
                                <Check size={13} /> Save
                            </Button>
                            <Button
                                onClick={() => { setAdding(false); setNewLabel(""); }}
                                size="sm"
                                variant="ghost"
                                disabled={busy}
                                data-testid="invoice-categories-add-cancel"
                            >
                                <X size={13} />
                            </Button>
                        </div>
                    )}

                    {!loading && cats && cats.length > 0 && (
                        <ul className="divide-y divide-slate-100 rounded-md border border-slate-200"
                            data-testid="invoice-categories-list">
                            {cats.map((c) => (
                                <li key={c.id}
                                    data-testid={`invoice-categories-row-${c.id}`}
                                    className="flex items-center justify-between gap-3 px-3 py-2">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        {editingId === c.id ? (
                                            <>
                                                <Input
                                                    ref={renameInputRef}
                                                    value={editLabel}
                                                    onChange={(e) => setEditLabel(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") submitRename(c.id);
                                                        if (e.key === "Escape") setEditingId(null);
                                                    }}
                                                    disabled={busy}
                                                    className="h-7 text-sm"
                                                    data-testid={`invoice-categories-rename-input-${c.id}`}
                                                    maxLength={80}
                                                />
                                                <Button
                                                    onClick={() => submitRename(c.id)}
                                                    size="sm"
                                                    disabled={busy || !editLabel.trim()}
                                                    className="h-7 bg-blue-900 hover:bg-blue-800 gap-1"
                                                    data-testid={`invoice-categories-rename-save-${c.id}`}
                                                >
                                                    <Check size={12} />
                                                </Button>
                                                <Button
                                                    onClick={() => setEditingId(null)}
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-7"
                                                    disabled={busy}
                                                    data-testid={`invoice-categories-rename-cancel-${c.id}`}
                                                >
                                                    <X size={12} />
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <span className="text-sm text-slate-900 font-medium truncate"
                                                      data-testid={`invoice-categories-label-${c.id}`}>
                                                    {c.label}
                                                </span>
                                                {!c.deletable && (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 ring-1 ring-slate-200 px-2 py-0.5 text-[10px] font-semibold"
                                                          data-testid={`invoice-categories-locked-${c.id}`}>
                                                        <Lock size={9} /> Locked
                                                    </span>
                                                )}
                                                {c.is_prefill && (
                                                    <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-900 ring-1 ring-blue-200 px-2 py-0.5 text-[10px] font-semibold"
                                                          data-testid={`invoice-categories-prefill-${c.id}`}>
                                                        Prefill
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    {isSuper && editingId !== c.id && (
                                        <div className="flex items-center gap-1 shrink-0">
                                            <Button
                                                onClick={() => { setEditingId(c.id); setEditLabel(c.label); }}
                                                size="sm"
                                                variant="ghost"
                                                className="gap-1 text-slate-600 hover:text-blue-900"
                                                disabled={busy}
                                                data-testid={`invoice-categories-rename-open-${c.id}`}
                                            >
                                                <Pencil size={12} /> Rename
                                            </Button>
                                            {c.deletable && (
                                                <Button
                                                    onClick={() => { setConfirmDelete({ id: c.id, label: c.label }); setDeleteError(null); }}
                                                    size="sm"
                                                    variant="ghost"
                                                    className="gap-1 text-red-700 hover:bg-red-50"
                                                    disabled={busy}
                                                    data-testid={`invoice-categories-delete-open-${c.id}`}
                                                >
                                                    <Trash2 size={12} /> Delete
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}

                    {!loading && cats && cats.length === 0 && (
                        <p className="text-sm text-slate-500 italic"
                           data-testid="invoice-categories-empty">
                            No categories configured yet.
                        </p>
                    )}
                </CardContent>
            </Card>

            <Dialog
                open={!!confirmDelete}
                onOpenChange={(o) => { if (!o) { setConfirmDelete(null); setDeleteError(null); } }}
            >
                <DialogContent data-testid="invoice-categories-delete-dialog">
                    <DialogHeader>
                        <DialogTitle>Delete category &ldquo;{confirmDelete?.label}&rdquo;?</DialogTitle>
                        <DialogDescription>
                            This cannot be undone. New invoices will no longer offer this
                            category in the dropdown.
                        </DialogDescription>
                    </DialogHeader>
                    {deleteError && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                             data-testid="invoice-categories-delete-error">
                            {deleteError}
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            variant="ghost"
                            onClick={() => { setConfirmDelete(null); setDeleteError(null); }}
                            disabled={busy}
                            data-testid="invoice-categories-delete-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={submitDelete}
                            disabled={busy}
                            className="bg-red-700 hover:bg-red-800 gap-1.5"
                            data-testid="invoice-categories-delete-confirm"
                        >
                            {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
